/**
 * TermsUpdateHost — asks, once, for agreement to the current Terms of Service and Privacy Policy (SOC 2 Phase 1,
 * decision 4, 2026-10-13 — docs/compliance/soc2-codebase-audit-report.md §5).
 *
 * WHO IS ASKED. A signed-in account with no server record (users/{uid}/terms_consents) of the documents this build
 * shows (src/config/legalVersions.ts): every account made before records existed (2026-10-12), and every account
 * when the documents' dates change. And, on request, a brand-new account that reached the app without ticking the
 * box (src/lib/legal/termsDialogRequest.ts: Google from the log-in card, /auth/callback).
 *
 * HOW IT STAYS OUT OF THE WAY (Miles, 2026-10-13: no slower pages, no worse experience):
 *   - it decides only after the first paint and an idle moment, and holds while an answer is arriving, a file is
 *     uploading, the workspace switch is on screen, or the person is on a live voice or desktop page
 *     (termsUpdateDecision.ts);
 *   - a device that knows the account agreed to the current documents makes no read at all; otherwise ONE bounded
 *     read of the person's own records, which the owner may read and nobody but the server may write;
 *   - the dialog is its own chunk, downloaded only when it may be shown;
 *   - accepting closes the dialog at once: the record is sent with the existing retries and, if the network is down,
 *     waits in this browser for the next page load (the pending note), which this host now sends on every page.
 * It never touches routines, scheduled sends, phone, SMS or voice: those keep running, and the person meets the
 * dialog on their next web visit. Lever: REACT_APP_TERMS_UPDATE_PROMPT_ACCOUNTS (deploy.yml, `*` by default).
 *
 * WHICH DIALOG. Where the `terms-update` surface shows (config/site-design.json), the new design's
 * (src/design/pavilion/terms/PavilionTermsDialog.tsx, its own chunk `pavilion-terms`, through withPavilion.tsx's door);
 * elsewhere today's (./TermsUpdateDialog.tsx, the `terms-update` chunk, through ./termsUpdateDoor.ts). Both take the same
 * props and say termsUpdateWords.ts's words, so accepting and signing out are this host's either way. Each design's
 * chunks are fetched at the first idle moment once the host may ask, and only that design's: today's dialog is fetched
 * where the new one shows only if the new one's chunks cannot be had (the door's fallback), so a person can always answer.
 * A person who has agreed, and a visitor who is signed out, fetch neither.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { buildEnv } from '../../config/buildEnv';
import { formatEffectiveDate, LEGAL_VERSIONS } from '../../config/legalVersions';
import { isAnyTabStreaming } from '../GlobalChatSystem/streamingFlag';
import { isAnyUploadMoving } from '../../utils/attachmentUploadProgress';
import { getWorkspaceTransitionState, readBootTransition } from '../../services/workspaceTransition';
import {
  cacheTermsAcceptance,
  flushPendingTermsConsent,
  hasCachedTermsAcceptance,
  hasPendingTermsConsentFor,
  sendTermsConsent,
} from '../../lib/legal/termsConsent';
import { decideTermsUpdate, termsPromptLeverOn, type TermsRecordState } from '../../lib/legal/termsUpdateDecision';
import { serveTermsDialogRequests, type TermsDialogRequest } from '../../lib/legal/termsDialogRequest';
import type { TermsConsentSignInMethod } from '../../lib/legal/termsConsentVocabulary';
import { designShows } from '../../design/previewPath';
import { PavilionTermsDoor, preloadPavilionTermsDialog } from '../../design/pavilion/withPavilion';
import { useTermsUpdateDialogModule } from './termsUpdateDoor';
// A TYPE-only import: erased at build, never an edge into the dialog's chunk.
import type { TermsUpdateDialogProps } from './TermsUpdateDialog';

/** Re-check a held prompt this often (the update banner's beat). */
const HOLD_RECHECK_MS = 5_000;
/** The one read's bound: past it, no prompt this session. */
const RECORD_READ_TIMEOUT_MS = 5_000;
/** The longest an idle start may wait on a busy main thread. */
const IDLE_TIMEOUT_MS = 2_000;

type Open =
  | { mode: 'update' }
  | { mode: 'new-account'; request: TermsDialogRequest; answer: (a: 'accepted' | 'signed-out') => void };

function signInMethodOf(user: FirebaseUser | null): TermsConsentSignInMethod {
  const providers = (user?.providerData ?? []).map((p) => p?.providerId);
  if (providers.includes('google.com')) return 'google';
  if (providers.includes('password')) return 'password';
  return 'other';
}

/** Does the person's own record of the CURRENT documents exist? One bounded server read. */
async function readCurrentRecord(uid: string): Promise<TermsRecordState> {
  try {
    const [fs, { default: db }] = await Promise.all([import('firebase/firestore'), import('../../utils/firestore')]);
    const q = fs.query(
      fs.collection(db, 'users', uid, 'terms_consents'),
      fs.where('tosVersion', '==', LEGAL_VERSIONS.terms.effectiveDate),
      fs.where('privacyVersion', '==', LEGAL_VERSIONS.privacy.effectiveDate),
      fs.limit(1),
    );
    const read = fs.getDocsFromServer(q);
    const snap = await Promise.race([
      read,
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('terms-record-timeout')), RECORD_READ_TIMEOUT_MS)),
    ]);
    return snap.empty ? 'absent' : 'present';
  } catch {
    return 'unreadable';
  }
}

function onIdle(run: () => void): () => void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  };
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(run, { timeout: IDLE_TIMEOUT_MS });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(run, 1_200);
  return () => window.clearTimeout(id);
}

/**
 * Today's dialog through its own door: the new design's fallback, drawn only if the new design's chunks cannot be had,
 * so its chunk is fetched only then.
 */
const TodaysTermsDialog: React.FC<TermsUpdateDialogProps> = (props) => {
  const dialog = useTermsUpdateDialogModule(true, true);
  if (!dialog) return null;
  const Dialog = dialog.TermsUpdateDialog;
  return <Dialog {...props} />;
};

export const TermsUpdateHost: React.FC = () => {
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [appReady, setAppReady] = useState(false);
  const [record, setRecord] = useState<TermsRecordState>('unknown');
  const [open, setOpen] = useState<Open | null>(null);
  const [, setBeat] = useState(0);
  // Accounts that accepted in this tab. The agreement's own memory lives in localStorage (the pending note, then the
  // device memory), but a browser that refuses storage must never see the dialog again after "accept".
  const acceptedHere = useRef<Set<string>>(new Set());

  useEffect(() => onAuthStateChanged(getAuth(), (u) => {
    setUser(u && !u.isAnonymous ? u : null);
    setRecord('unknown');
  }), []);
  useEffect(() => onIdle(() => setAppReady(true)), []);
  // A sign-in flow's new account, shown at once whatever the page (the flow is waiting on the answer).
  useEffect(() => serveTermsDialogRequests((request, answer) => setOpen({ mode: 'new-account', request, answer })), []);

  const uid = user?.uid ?? null;

  // A record that waited in this browser goes now, on any page (it used to go only from ProtectedRoute).
  useEffect(() => {
    if (appReady && uid && hasPendingTermsConsentFor(uid)) void flushPendingTermsConsent(uid);
  }, [appReady, uid]);

  const now = Date.now();
  const decision = decideTermsUpdate({
    appReady,
    signedIn: Boolean(uid),
    leverOn: termsPromptLeverOn(uid, buildEnv().REACT_APP_TERMS_UPDATE_PROMPT_ACCOUNTS),
    pathname,
    cached: uid ? acceptedHere.current.has(uid) || hasCachedTermsAcceptance(uid) : false,
    pending: uid ? hasPendingTermsConsentFor(uid) : false,
    record,
    streaming: isAnyTabStreaming(now),
    uploading: isAnyUploadMoving(now),
    switchingWorkspace: Boolean(getWorkspaceTransitionState() || readBootTransition()),
  });

  useEffect(() => {
    if (decision.action !== 'read' || !uid) return undefined;
    let cancelled = false;
    void readCurrentRecord(uid).then((state) => {
      if (cancelled) return;
      if (state === 'present') cacheTermsAcceptance(uid);
      setRecord(state);
    });
    return () => { cancelled = true; };
  }, [decision.action, uid]);

  useEffect(() => {
    if (decision.action !== 'hold') return undefined;
    const t = window.setInterval(() => setBeat((n) => n + 1), HOLD_RECHECK_MS);
    return () => window.clearInterval(t);
  }, [decision.action]);

  useEffect(() => {
    if (decision.action === 'ask' && !open) setOpen({ mode: 'update' });
  }, [decision.action, open]);

  const mayAsk = decision.action === 'read' || decision.action === 'hold' || decision.action === 'ask';
  // A page never changes address without reloading, so the design is fixed for the life of the page (previewPath.ts).
  const newDesign = designShows('terms-update');
  // Today's chunk where today's dialog is drawn; the new design's chunks where it is (today's then only as its fallback).
  const dialog = useTermsUpdateDialogModule(!newDesign && mayAsk, !newDesign && Boolean(open));
  useEffect(() => (newDesign && mayAsk ? onIdle(preloadPavilionTermsDialog) : undefined), [newDesign, mayAsk]);
  const effectiveLabel = useMemo(() => formatEffectiveDate(LEGAL_VERSIONS.terms.effectiveDate), []);

  const accept = useCallback(() => {
    if (!open) return;
    const forUid = open.mode === 'new-account' ? open.request.uid : uid;
    if (forUid) {
      acceptedHere.current.add(forUid);
      // The pending note is written before the first await, so the agreement survives a closed tab from here on.
      void sendTermsConsent({
        uid: forUid,
        surface: 'terms-dialog',
        signInMethod: open.mode === 'new-account' ? open.request.signInMethod : signInMethodOf(user),
        acceptedAtMs: Date.now(),
      });
    }
    if (open.mode === 'new-account') open.answer('accepted');
    setOpen(null);
  }, [open, uid, user]);

  const signOut = useCallback(() => {
    if (open?.mode === 'new-account') open.answer('signed-out');
    setOpen(null);
    logout({ returnTo: '/' });
  }, [open, logout]);

  if (!open) return null;
  const props: TermsUpdateDialogProps = { mode: open.mode, effectiveLabel, onAccept: accept, onSignOut: signOut };
  if (newDesign) return <PavilionTermsDoor {...props} fallback={<TodaysTermsDialog {...props} />} />;
  if (!dialog) return null;
  const Dialog = dialog.TermsUpdateDialog;
  return <Dialog {...props} />;
};

export default TermsUpdateHost;
